/**
 * 현장 설정 파일 패널의 표시 규칙 — 화면과 떼어 테스트한다.
 */
import type { SiteConfigFile, SiteConfigSite } from '../../../services/SiteConfigService';
import type { SiteLiveConfigFile, SiteLiveStatus } from '../../../services/SiteHealthService';

/** 저장소 파일과 현장이 지금 읽는 파일의 비교 결과. unknown = 현장을 아직 못 읽음. */
export type LiveConfigCompare = 'same' | 'different' | 'site_missing' | 'no_container' | 'no_var' | 'unknown';

export const LIVE_CONFIG_TEXT: Record<LiveConfigCompare, string> = {
  same: '현장과 같음',
  different: '현장과 다름',
  site_missing: '현장에 없음',
  no_container: '현장에 서비스 없음',
  no_var: '현장 파일 위치 모름',
  unknown: '',
};

/** 현장 점검 결과에서 이 방재실(room = live group)·서비스·파일의 현장 실제 파일. */
export function findLiveConfig(
  liveSites: Pick<SiteLiveStatus, 'group' | 'configs'>[],
  room: string,
  app: string,
  file: string,
): SiteLiveConfigFile | undefined {
  return liveSites
    .find((s) => (s.group ?? '').trim() === room.trim())
    ?.configs?.find((c) => c.app === app && c.file === file);
}

/** 현장 점검 열(live group)에 해당하는 저장소 설정 폴더 — 방재실 이름(room)이 같은 것. 하나면 둘 다 ''. */
export function configSiteForGroup<T extends Pick<SiteConfigSite, 'room'>>(sites: T[], group: string | undefined): T | undefined {
  const g = (group ?? '').trim();
  return sites.find((s) => s.room.trim() === g);
}

/** 저장소 sha 와 현장 sha 를 견준다 — 둘 다 git blob sha 라 같으면 바이트까지 같다. 둘 다 없으면 같음. */
export function compareLiveConfig(
  repo: Pick<SiteConfigFile, 'exists' | 'sha'>,
  live: Pick<SiteLiveConfigFile, 'state' | 'sha'> | undefined,
): LiveConfigCompare {
  if (!live) return 'unknown';
  if (live.state === 'no_container' || live.state === 'no_var') return live.state;
  if (live.state === 'missing') return repo.exists ? 'site_missing' : 'same';
  return repo.exists && !!repo.sha && repo.sha === live.sha ? 'same' : 'different';
}

/** 파일 크기 — 설정 파일은 수 KB 라 B/KB 만. 없는 파일은 '없음'. */
export function formatConfigSize(file: Pick<SiteConfigFile, 'exists' | 'size'>): string {
  if (!file.exists) return '없음';
  if (file.size < 1024) return `${file.size} B`;
  return `${(file.size / 1024).toFixed(1)} KB`;
}

/** 방재실 탭 이름 — 방재실이 하나면 이름이 비어 있어 폴더 코드로 대신한다. */
export function siteConfigTabLabel(site: Pick<SiteConfigSite, 'room' | 'code'>): string {
  const room = site.room.trim();
  if (room && site.code) return `${room} · ${site.code}`;
  return room || site.code || '설정 코드 없음';
}

/** 커밋 메시지는 첫 줄만 — 본문에는 고친 사람이 들어 있다. */
export function commitTitle(message: string): string {
  return message.split('\n')[0].trim();
}
